import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, TextField, MenuItem } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGetChequeQuery } from '../../../store/api/financeApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

// Standard cheque slip: 6" × 2.75" = 152mm × 70mm (common bank slip size)
const CHEQUE_SIZE_OPTIONS = [
  { value: 'standard', label: 'Standard cheque slip (6" × 2.75" / 152×70mm)', width: 152, height: 70, unit: 'mm', pageSize: '152mm 70mm' },
  { value: 'a5', label: 'A5 (148 × 210 mm)', width: 148, height: 210, unit: 'mm', pageSize: 'A5' },
  { value: 'a4', label: 'A4 (210 × 297 mm)', width: 210, height: 297, unit: 'mm', pageSize: 'A4' },
];
const STORAGE_KEY = 'chequePrintSize';

function amountToWords(num) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  if (num === 0) return 'Zero';
  const n = Math.floor(Math.abs(num));
  function toWords(x) {
    if (x === 0) return '';
    if (x < 20) return ones[x];
    if (x < 100) return tens[Math.floor(x / 10)] + (x % 10 ? ' ' + ones[x % 10] : '');
    if (x < 1000) return ones[Math.floor(x / 100)] + ' Hundred' + (x % 100 ? ' ' + toWords(x % 100) : '');
    if (x < 100000) return toWords(Math.floor(x / 1000)) + ' Thousand' + (x % 1000 ? ' ' + toWords(x % 1000) : '');
    if (x < 10000000) return toWords(Math.floor(x / 100000)) + ' Lakh' + (x % 100000 ? ' ' + toWords(x % 100000) : '');
    if (x < 1e9) return toWords(Math.floor(x / 1e7)) + ' Crore' + (x % 1e7 ? ' ' + toWords(x % 1e7) : '');
    return num.toFixed(2);
  }
  const whole = Math.floor(n);
  const frac = Math.round((num - whole) * 100);
  let s = toWords(whole).trim() || 'Zero';
  if (frac > 0) s += ' and ' + frac + '/100';
  return s;
}

function ChequePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sizeKey, setSizeKey] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'standard';
    } catch {
      return 'standard';
    }
  });
  const { data: cheque, isLoading } = useGetChequeQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const sizeOption = CHEQUE_SIZE_OPTIONS.find((o) => o.value === sizeKey) || CHEQUE_SIZE_OPTIONS[0];

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, sizeKey);
    } catch (_) {}
  }, [sizeKey]);

  const formatAmount = (v) => {
    const n = Number(v || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
    } catch {
      return `${currency} ${n.toFixed(2)}`;
    }
  };
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { dateStyle: 'long' }) : '–');

  const handlePrint = () => window.print();

  if (isLoading || !cheque) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const amountWords = amountToWords(Number(cheque.amount || 0));

  const isStandard = sizeKey === 'standard';
  const containerWidth = sizeOption.unit === 'mm' ? `${sizeOption.width}mm` : `${sizeOption.width}mm`;
  const containerMinHeight = sizeOption.unit === 'mm' ? `${sizeOption.height}mm` : '200px';

  const formatDateShort = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–');

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
      <style>{`
        @media print {
          @page { size: ${sizeOption.pageSize}; margin: ${isStandard ? '3mm' : '8mm'}; }
          body * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      <Box sx={{ mb: 2, '@media print': { display: 'none' } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/finance/cheques/${id}`)} sx={{ mr: 1 }}>
          Back
        </Button>
        <TextField
          select
          size="small"
          label="Print size"
          value={sizeKey}
          onChange={(e) => setSizeKey(e.target.value)}
          sx={{ minWidth: 280, mr: 2 }}
        >
          {CHEQUE_SIZE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
          Print
        </Button>
      </Box>

      {isStandard ? (
        <Box
          component="div"
          className="cheque-slip-standard"
          sx={{
            width: containerWidth,
            minHeight: containerMinHeight,
            maxWidth: '100%',
            margin: '0 auto',
            border: '1px solid #000',
            boxSizing: 'border-box',
            p: '4px 6px',
            fontFamily: 'Arial, sans-serif',
            fontSize: '9px',
            lineHeight: 1.2,
            '@media print': {
              width: containerWidth,
              minHeight: containerMinHeight,
              border: '1px solid #000',
              p: '3mm 4mm',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #ccc', pb: '2px', mb: '2px' }}>
            <Box>
              <Box component="span" sx={{ fontWeight: 700, fontSize: '10px' }}>{company?.name || 'Company'}</Box>
              <Box component="div" sx={{ fontSize: '8px', color: '#444' }}>
                {cheque.bankAccount?.name}{cheque.bankAccount?.accountNumber ? ` A/c: ${cheque.bankAccount.accountNumber}` : ''}
                {cheque.bankAccount?.bankName ? ` · ${cheque.bankAccount.bankName}` : ''}
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Box component="span" sx={{ fontSize: '8px', color: '#444' }}>Date </Box>
              <Box component="span" sx={{ fontWeight: 600 }}>{formatDateShort(cheque.chequeDate)}</Box>
              <Box component="div" sx={{ fontSize: '8px' }}>No. {cheque.chequeNumber}</Box>
            </Box>
          </Box>
          <Box sx={{ mb: '2px' }}>
            <Box component="span" sx={{ fontSize: '8px', color: '#444' }}>Pay to the order of </Box>
            <Box component="span" sx={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '60%', fontWeight: 600 }}>
              {cheque.payee || ' '}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: '4px' }}>
            <Box sx={{ flex: 1, borderBottom: '1px solid #000', minHeight: '14px', fontSize: '8px' }}>
              {amountWords} {currency}
            </Box>
            <Box sx={{ border: '1px solid #000', px: '6px', py: '2px', fontWeight: 700, fontSize: '10px', whiteSpace: 'nowrap' }}>
              {formatAmount(cheque.amount)}
            </Box>
          </Box>
          {cheque.memo && (
            <Box sx={{ mt: '2px', fontSize: '8px', color: '#555' }}>Memo: {cheque.memo}</Box>
          )}
        </Box>
      ) : (
        <Box
          component="div"
          sx={{
            width: containerWidth,
            maxWidth: '100%',
            minHeight: containerMinHeight,
            border: '2px solid #333',
            p: 3,
            position: 'relative',
            mx: 'auto',
            '@media print': {
              border: '1px solid #000',
              boxShadow: 'none',
              width: containerWidth,
              minHeight: containerMinHeight,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{company?.name || 'Company'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {cheque.bankAccount?.name || 'Bank'}
                {cheque.bankAccount?.accountNumber ? ` · A/c ${cheque.bankAccount.accountNumber}` : ''}
                {cheque.bankAccount?.bankName ? ` · ${cheque.bankAccount.bankName}` : ''}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>Cheque # {cheque.chequeNumber}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Date</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDate(cheque.chequeDate)}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Pay to the order of</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, borderBottom: '1px solid #333', pb: 0.5, minHeight: 28 }}>
              {cheque.payee || '–'}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Amount (words)</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, borderBottom: '1px solid #333', pb: 0.5, minHeight: 28 }}>
              {amountWords} {currency}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 3 }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>{formatAmount(cheque.amount)}</Typography>
            {cheque.memo && (
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 240, textAlign: 'right' }}>{cheque.memo}</Typography>
            )}
          </Box>
          <Box sx={{ position: 'absolute', bottom: 12, right: 16, fontSize: '11px', color: '#666' }}>Status: {cheque.status}</Box>
        </Box>
      )}
    </Box>
  );
}

export default ChequePrint;
